import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { aiService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const TABS = [
  { key: "providers", label: "Providers" },
  { key: "models", label: "Models" },
  { key: "usage", label: "Usage Logs" },
  { key: "audit", label: "Audit Logs" },
];

const EMPTY_FORM = { apiKey: "", defaultModel: "", baseUrl: "", enabled: false, isDefault: false };

export default function AiFoundationPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("AI_CONFIG.EDIT");
  const [tab, setTab] = useState("providers");
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [usage, setUsage] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  // Configure-provider modal state
  const [configProvider, setConfigProvider] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, u, a] = await Promise.all([
        aiService.providers(), aiService.models(), aiService.usage(), aiService.audit(),
      ]);
      setProviders(p); setModels(m); setUsage(u.items || []); setAudit(a.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConfig = (p) => {
    setConfigProvider(p);
    setTestResult(null);
    setForm({
      apiKey: "",
      defaultModel: p.defaultModel || "",
      baseUrl: p.baseUrl || "",
      enabled: !!p.enabled,
      isDefault: !!p.isDefault,
    });
  };

  const providerModels = models.filter((m) => m.providerId === configProvider?.id);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const body = {
        defaultModel: form.defaultModel || undefined,
        baseUrl: form.baseUrl || undefined,
        enabled: form.enabled,
        isDefault: form.isDefault,
      };
      if (form.apiKey.trim()) body.apiKey = form.apiKey.trim(); // only send when changed
      await aiService.updateProvider(configProvider.id, body);
      toast.success("AI provider configuration saved");
      setConfigProvider(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Persist first so the test reflects the values on screen.
      const body = { enabled: true, isDefault: true };
      if (form.apiKey.trim()) body.apiKey = form.apiKey.trim();
      if (form.defaultModel) body.defaultModel = form.defaultModel;
      if (form.baseUrl) body.baseUrl = form.baseUrl;
      await aiService.updateProvider(configProvider.id, body);
      const res = await aiService.testProvider(configProvider.id);
      setTestResult(res);
      if (res.connected) toast.success(`Connected · ${res.model} (${res.source})`);
      else toast.error(`Connection failed${res.error ? ` · ${res.error}` : ""}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const toggleProvider = async (p, field) => {
    try {
      await aiService.updateProvider(p.id, { [field]: !p[field] });
      toast.success("Provider updated");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    }
  };

  const toggleModel = async (m) => {
    try {
      await aiService.updateModel(m.id, { enabled: !m.enabled });
      toast.success("Model updated");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    }
  };

  const providerCols = [
    { key: "displayName", header: "Provider", render: (r) => <span className="font-medium text-foreground">{r.displayName}</span> },
    { key: "provider", header: "Type", render: (r) => <Badge color="teal">{r.provider}</Badge> },
    { key: "defaultModel", header: "Model", render: (r) => <span className="font-mono text-xs text-foreground">{r.defaultModel || "gpt-4o (default)"}</span> },
    { key: "hasApiKey", header: "API Key", render: (r) => (r.hasApiKey ? <Badge color="green">Set</Badge> : <Badge color="slate">Env / none</Badge>) },
    { key: "isDefault", header: "Default", render: (r) => (r.isDefault ? <Badge color="blue">Default</Badge> : "—") },
    {
      key: "enabled", header: "Status",
      render: (r) => (
        <button disabled={!canEdit} onClick={() => toggleProvider(r, "enabled")} data-testid={`provider-toggle-${r.id}`}>
          <Badge color={r.enabled ? "green" : "slate"}>{r.enabled ? "Enabled" : "Disabled"}</Badge>
        </button>
      ),
    },
    {
      key: "configure", header: "",
      render: (r) => (
        <Button size="sm" variant="outline" disabled={!canEdit} onClick={() => openConfig(r)} data-testid={`provider-configure-${r.id}`}>
          Configure
        </Button>
      ),
    },
  ];

  const modelCols = [
    { key: "modelKey", header: "Model", render: (r) => <span className="font-mono text-sm text-foreground">{r.modelKey}</span> },
    { key: "purpose", header: "Purpose", render: (r) => <Badge color="slate">{r.purpose}</Badge> },
    { key: "maxTokens", header: "Max Tokens" },
    { key: "temperature", header: "Temp" },
    {
      key: "enabled", header: "Status",
      render: (r) => (
        <button disabled={!canEdit} onClick={() => toggleModel(r)} data-testid={`model-toggle-${r.id}`}>
          <Badge color={r.enabled ? "green" : "slate"}>{r.enabled ? "Enabled" : "Disabled"}</Badge>
        </button>
      ),
    },
  ];

  const usageCols = [
    { key: "provider", header: "Provider" },
    { key: "modelKey", header: "Model" },
    { key: "totalTokens", header: "Tokens" },
    { key: "status", header: "Status" },
    { key: "createdOn", header: "When", render: (r) => (r.createdOn || "").slice(0, 16).replace("T", " ") },
  ];

  const auditCols = [
    { key: "action", header: "Action" },
    { key: "provider", header: "Provider" },
    { key: "decision", header: "Decision" },
    { key: "createdOn", header: "When", render: (r) => (r.createdOn || "").slice(0, 16).replace("T", " ") },
  ];

  return (
    <div data-testid="ai-foundation-page">
      <PageHeader
        title="AI Foundation"
        subtitle="Provider & model configuration, API keys, usage and governance logs"
      />
      <div className="rounded-lg bg-muted/50 border border-border px-4 py-2.5 text-xs text-muted-foreground mb-5">
        The <strong>default, enabled</strong> provider's API key and model power the AI Copilot &amp; agents.
        When no provider is enabled, the platform uses the deployment's <code>OPENAI_API_KEY</code> with model
        <code> gpt-4o</code>. Configure a provider below to change the model/key without a redeploy. Keys are
        encrypted at rest and never displayed.
      </div>

      <div className="flex gap-1 mb-5 border-b border-border" data-testid="ai-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`ai-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "providers" && <DataTable columns={providerCols} rows={providers} loading={loading} testid="ai-providers-table" />}
      {tab === "models" && <DataTable columns={modelCols} rows={models} loading={loading} testid="ai-models-table" />}
      {tab === "usage" && <DataTable columns={usageCols} rows={usage} loading={loading} testid="ai-usage-table" empty="No usage logged yet" />}
      {tab === "audit" && <DataTable columns={auditCols} rows={audit} loading={loading} testid="ai-audit-table" empty="No AI audit logs yet" />}

      {/* Configure provider modal */}
      <Dialog open={!!configProvider} onOpenChange={(o) => !o && setConfigProvider(null)}>
        <DialogContent data-testid="provider-config-modal">
          <DialogHeader>
            <DialogTitle>Configure {configProvider?.displayName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cfg-key">API Key</Label>
              <Input
                id="cfg-key" type="password" autoComplete="off"
                placeholder={configProvider?.hasApiKey ? "•••••••• (leave blank to keep current)" : "sk-..."}
                value={form.apiKey}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                data-testid="provider-apikey-input"
              />
              <p className="text-xs text-muted-foreground">Stored encrypted. Leave blank to keep the existing key.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cfg-model">Default Model</Label>
              <Input
                id="cfg-model" list="cfg-model-list" placeholder="gpt-4o"
                value={form.defaultModel}
                onChange={(e) => setForm({ ...form, defaultModel: e.target.value })}
                data-testid="provider-model-input"
              />
              <datalist id="cfg-model-list">
                {providerModels.map((m) => <option key={m.id} value={m.modelKey} />)}
                <option value="gpt-4o" />
                <option value="gpt-4o-mini" />
              </datalist>
              <p className="text-xs text-muted-foreground">Must be a model enabled for this API key. Blank = gpt-4o.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cfg-base">Base URL (optional)</Label>
              <Input
                id="cfg-base" placeholder="https://api.openai.com/v1"
                value={form.baseUrl}
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                data-testid="provider-baseurl-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="cfg-enabled" className="cursor-pointer">Enabled (use this provider)</Label>
              <Switch id="cfg-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="provider-enabled-switch" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="cfg-default" className="cursor-pointer">Set as default provider</Label>
              <Switch id="cfg-default" checked={form.isDefault} onCheckedChange={(v) => setForm({ ...form, isDefault: v })} data-testid="provider-default-switch" />
            </div>

            {testResult && (
              <div
                className={`rounded-md px-3 py-2 text-sm ${testResult.connected ? "bg-green-500/10 text-green-700" : "bg-red-500/10 text-red-700"}`}
                data-testid="provider-test-result"
              >
                {testResult.connected
                  ? `Connected · model ${testResult.model} · source ${testResult.source}`
                  : `Failed${testResult.error ? ` · ${testResult.error}` : ""} (source ${testResult.source})`}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={testConnection} disabled={testing || saving} data-testid="provider-test-button">
              {testing ? "Testing…" : "Save & Test connection"}
            </Button>
            <Button onClick={saveConfig} disabled={saving || testing} data-testid="provider-save-button">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
